import { GlassCard } from '@/components/ui/glass-card';
import { cn } from '@/lib/utils';
import {
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
  type LucideIcon
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

interface VitalTrendCardProps {
  label: string;
  value: number | string;
  unit: string;
  trend: 'improving' | 'stable' | 'worsening' | 'unknown';
  status: 'normal' | 'elevated' | 'high' | 'low' | 'critical';
  sparklineData?: number[];
  icon?: LucideIcon;
  onClick?: () => void;
}

const trendConfig = {
  improving: {
    icon: TrendingUp,
    text: 'Improving',
    color: 'text-green-600 dark:text-green-400',
  },
  stable: {
    icon: Minus,
    text: 'Stable',
    color: 'text-gray-600 dark:text-gray-400',
  },
  worsening: {
    icon: TrendingDown,
    text: 'Worsening',
    color: 'text-red-600 dark:text-red-400',
  },
  unknown: {
    icon: Minus,
    text: 'Unknown',
    color: 'text-gray-400 dark:text-gray-500',
  },
};

const statusConfig = {
  normal: {
    color: 'bg-green-500',
    chartColor: '#10b981',
  },
  elevated: {
    color: 'bg-amber-500',
    chartColor: '#f59e0b',
  },
  high: {
    color: 'bg-red-500',
    chartColor: '#ef4444',
  },
  low: {
    color: 'bg-blue-500',
    chartColor: '#3b82f6',
  },
  critical: {
    color: 'bg-red-600',
    chartColor: '#dc2626',
  },
};

export default function VitalTrendCard({
  label,
  value,
  unit,
  trend,
  status,
  sparklineData,
  icon: Icon = Activity,
  onClick,
}: VitalTrendCardProps) {
  const TrendIcon = trendConfig[trend].icon;
  const trendStyle = trendConfig[trend];
  const statusStyle = statusConfig[status];

  // Prepare data for chart
  const chartData = sparklineData?.map((val, idx) => ({ value: val, index: idx })) ||
    Array.from({ length: 10 }, (_, idx) => ({ value: 0, index: idx }));

  // Detect anomalies (values that deviate significantly from mean)
  const hasAnomaly = sparklineData && sparklineData.length > 0 ? (() => {
    const mean = sparklineData.reduce((a, b) => a + b, 0) / sparklineData.length;
    const stdDev = Math.sqrt(
      sparklineData.reduce((sq, n) => sq + Math.pow(n - mean, 2), 0) / sparklineData.length
    );
    return sparklineData.some(val => Math.abs(val - mean) > 2 * stdDev);
  })() : false;

  return (
    <GlassCard
      variant="subtle"
      padding="sm"
      className={cn(
        "min-w-[160px] flex-shrink-0 cursor-pointer transition-all hover:shadow-md",
        onClick && "hover:scale-[1.02]"
      )}
      onClick={onClick}
    >
      <div className="space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              {label}
            </span>
          </div>
          <div className={cn("h-2 w-2 rounded-full", statusStyle.color)} />
        </div>

        {/* Value */}
        <div className="space-y-1">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-bold text-gray-900 dark:text-white">
              {value}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {unit}
            </span>
          </div>

          {/* Trend */}
          <div className={cn("flex items-center gap-1 text-xs font-medium", trendStyle.color)}>
            <TrendIcon className="h-3 w-3" />
            <span>{trendStyle.text}</span>
          </div>
        </div>

        {/* Sparkline */}
        <div className="relative">
          {hasAnomaly && (
            <div className="absolute top-1 right-1 z-10">
              <div className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
            </div>
          )}
          <ResponsiveContainer width="100%" height={50}>
            <AreaChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id={`gradient-${label}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={statusStyle.chartColor} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={statusStyle.chartColor} stopOpacity={0} />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey="value"
                stroke={statusStyle.chartColor}
                strokeWidth={2}
                fill={`url(#gradient-${label})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </GlassCard>
  );
}
